import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Gauge,
  Search,
  ShieldAlert,
  Sun,
} from "lucide-react";
import { fetchSolarIssues, normalize, type SolarIssue } from "@/faults-app/lib/db";
import { AppShell, StatusBadge, severityToneKey } from "@/faults-app/components/app-shell";

export const Route = createFileRoute("/solar")({
  head: () => ({
    meta: [
      { title: "تشخيص أعطال أنظمة الطاقة الشمسية" },
      {
        name: "description",
        content:
          "شخّص أعطال الطاقة الشمسية بالعربي: الإنفرتر لا يعمل، البطارية لا تشحن، ضعف إنتاج الألواح، أخطاء MPPT، مع خطوات الفحص والقياسات والحل.",
      },
      { property: "og:title", content: "تشخيص أعطال أنظمة الطاقة الشمسية" },
      {
        property: "og:description",
        content: "اختر المشكلة أو اكتبها ليعرض النظام الأسباب المحتملة وخطوات الفحص والقياسات والحل.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: SolarPage,
});

function SolarPage() {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const { data: issues = [], isLoading } = useQuery({
    queryKey: ["solar-issues"],
    queryFn: fetchSolarIssues,
  });

  const q = normalize(query.trim());
  const list = q
    ? issues.filter((i) =>
        normalize(`${i.title_ar} ${i.description_ar} ${i.keywords} ${i.solution_ar}`).includes(q),
      )
    : issues;

  return (
    <AppShell>
      <main className="mx-auto w-full max-w-4xl px-4 pt-6">
        <header className="rounded-3xl panel-dark p-5 shadow-card">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Sun className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-extrabold text-white">
                تشخيص أعطال أنظمة الطاقة الشمسية
              </h1>
              <p className="truncate text-sm text-primary">اختر المشكلة أو اكتبها بكلماتك</p>
            </div>
          </div>

          <div className="mt-4 rounded-2xl bg-card p-3 shadow-card">
            <label className="mb-2 flex items-center gap-2 text-sm font-bold text-black" htmlFor="solar-q">
              <Search className="h-4 w-4 text-black" />
              اكتب المشكلة التي تواجهك...
            </label>
            <input
              id="solar-q"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="مثال: البطارية لا تشحن أو الإنفرتر يفصل"
              className="w-full rounded-xl border border-input bg-background p-3 text-base text-black caret-black outline-none transition placeholder:text-black/60 focus:border-primary focus:ring-3 focus:ring-primary/25"
            />
          </div>
        </header>

        <section className="mt-6 space-y-3 pb-8">
          {isLoading && <p className="p-6 text-center text-muted-foreground">جارٍ التحميل...</p>}
          {!isLoading && list.length === 0 && (
            <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              لا توجد نتائج مطابقة، جرّب كلمات مثل: شحن، بطارية، إنفرتر، ألواح، MPPT.
            </p>
          )}
          {list.map((i) => (
            <IssueCard
              key={i.id}
              item={i}
              open={openId === i.id}
              onToggle={() => setOpenId(openId === i.id ? null : i.id)}
            />
          ))}
        </section>
      </main>
    </AppShell>
  );
}

function IssueCard({
  item,
  open,
  onToggle,
}: {
  item: SolarIssue;
  open: boolean;
  onToggle: () => void;
}) {
  return (
    <article className="card-tech overflow-hidden">
      <button onClick={onToggle} className="press flex w-full items-center gap-3 p-4 text-right">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-linear-to-br from-primary to-primary-glow text-primary-foreground">
          <Sun className="h-6 w-6" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-extrabold">{item.title_ar}</span>
          <span className="mt-1 block truncate text-xs text-muted-foreground">
            {item.description_ar}
          </span>
        </span>
        <ArrowLeft
          className={`h-5 w-5 shrink-0 text-primary-dark transition-transform ${open ? "-rotate-90" : ""}`}
        />
      </button>

      {open && (
        <div className="animate-fade-in space-y-4 border-t border-border p-4 text-sm">
          <StatusBadge tone={severityToneKey(item.severity)}>
            <AlertTriangle className="h-3.5 w-3.5" /> درجة الخطورة: {item.severity}
          </StatusBadge>

          <div>
            <h3 className="mb-2 text-sm font-extrabold text-primary-dark">الأسباب المحتملة</h3>
            <ul className="space-y-3">
              {item.causes.map((c, i) => (
                <li key={i}>
                  <div className="flex justify-between text-sm font-bold">
                    <span className="min-w-0 pe-2">{c.cause}</span>
                    <span className="shrink-0 text-primary-dark">{c.probability}%</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${c.probability}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-2 flex items-center gap-2 text-sm font-extrabold text-primary-dark">
              <Gauge className="h-4 w-4" /> خطوات الفحص بالترتيب
            </h3>
            <ol className="space-y-2">
              {item.steps.map((s, i) => (
                <li key={i} className="flex gap-2 rounded-xl border border-border bg-muted/40 p-2">
                  <span className="font-extrabold text-primary-dark">{i + 1}.</span>
                  <span className="min-w-0 flex-1">{s}</span>
                </li>
              ))}
            </ol>
          </div>

          {item.measurements.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-extrabold text-primary-dark">القياسات المطلوبة</h3>
              <ul className="grid gap-2 sm:grid-cols-2">
                {item.measurements.map((m, i) => (
                  <li
                    key={i}
                    className="rounded-xl border border-dashed border-primary/40 bg-accent/40 p-2 text-xs font-bold"
                  >
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {item.solution_ar && (
            <div className="rounded-xl border border-success/40 bg-success/10 p-3">
              <h3 className="mb-1 flex items-center gap-2 text-sm font-extrabold text-success">
                <CheckCircle2 className="h-4 w-4" /> الحل المقترح
              </h3>
              <p className="leading-relaxed">{item.solution_ar}</p>
            </div>
          )}

          {item.safety_ar && (
            <p className="flex gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3 font-bold text-destructive">
              <ShieldAlert className="h-5 w-5 shrink-0" />
              <span>تحذير سلامة: {item.safety_ar}</span>
            </p>
          )}
        </div>
      )}
    </article>
  );
}
